import {Field} from 'formik';
import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Container,
    Fieldset,
    Loader,
    Text,
    Title,
} from '@mantine/core';
import {
    FormikSelect,
    FormikTextInput,
} from '@flexinet/ui-components';

import {useZoneCreateConnections} from '../hooks/useZoneCreateConnections';
import {providerLabel} from '../providerLabels';

// FNP-1191 CRUD half — the CREATE form fields.
//
// ⚠️ THERE IS NO `isPrivate` FIELD, AND THAT IS THE SCHEMA'S DECISION.
//
// A private zone is VPC-associated: Route 53 wants a VPC id and region at
// creation and Cloudflare has no equivalent. A field only one provider can
// populate is a provider leak wearing a neutral name, so `CreateDnsZoneInput`
// has no `isPrivate` and neither does this form. `DnsZone.isPrivate` stays a
// READ-time observation of a zone that exists, which is a different thing.
//
// ⚠️ AND NO NAMESERVERS FIELD. They are ASSIGNED by the provider at creation,
// not chosen. Offering an input would imply a control the tenant does not have.

export const FormComponent = () => {

    const {
        connections,
        loading,
        isError,
        hasAnyConnection,
    } = useZoneCreateConnections();

    // The two empty states are DIFFERENT problems with different fixes, and one
    // message for both sends half the tenants to the wrong place.
    const noConnectionsAtAll = !loading && !isError && !hasAnyConnection;
    const noWritableConnections = !loading && !isError && hasAnyConnection && connections.length === 0;

    return (
        <Container>
            <Fieldset
                legend={<Title order={3}><Text tt={'uppercase'}>{I18n.get('Zone')}</Text></Title>}
                px={'xl'}
                mb={'xl'}
            >
                {loading && <Loader size={'sm'}/>}

                {isError && (
                    <Alert color={'red'} title={I18n.get('Could not load your connections')}>
                        {I18n.get('The list of provider connections did not load, so there is nothing to create a zone through. Close this and try again.')}
                    </Alert>
                )}

                {noConnectionsAtAll && (
                    <Alert color={'yellow'} title={I18n.get('No provider connections')}>
                        {I18n.get('Connect a DNS provider in Settings before creating a zone. A zone is always created through a connection.')}
                    </Alert>
                )}

                {noWritableConnections && (
                    <Alert color={'yellow'} title={I18n.get('No connection can create zones')}>
                        {I18n.get('Your connections are read-only, or their credentials are not currently valid. Reconnect a provider in Settings with a credential that is allowed to create zones.')}
                    </Alert>
                )}

                {connections.length > 0 && (
                    <>
                        {/* ⚠️ REQUIRED, NEVER INFERRED. A zone is only
                            addressable through the connection that mints its
                            id, and a tenant with two Route 53 accounts has two
                            connections where the same name could live under
                            either. Defaulting to "the first one" would create
                            zones in the wrong account silently. */}
                        <Field
                            name={'connectionId'}
                            label={I18n.get('Connection')}
                            description={I18n.get('The zone is created through this connection, and can only be managed through it afterwards.')}
                            component={FormikSelect}
                            withAsterisk
                            data={connections.map(connection => ({
                                value: connection.id,
                                // The tenant's own name for it, plus the
                                // provider — never the opaque connection id.
                                label: `${connection.displayName} (${providerLabel(connection.providerId)})`,
                            }))}
                        />

                        <Field
                            name={'name'}
                            label={I18n.get('Zone name')}
                            description={I18n.get('The domain this zone will answer for, without a trailing dot — for example example.com')}
                            component={FormikTextInput}
                            withAsterisk
                        />

                        <Field
                            name={'comment'}
                            label={I18n.get('Comment')}
                            description={I18n.get('Optional. Recorded against the zone where the provider supports one, and ignored where it does not.')}
                            component={FormikTextInput}
                        />
                    </>
                )}
            </Fieldset>
        </Container>
    );
};
